import { motion } from "framer-motion";
import { ArrowRight, Award, Users, Clock } from "lucide-react";

const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_joinery-landing/artifacts/o51c66q4_kitchen.jpg";

export const HeroSection = ({ onGetQuoteClick }) => {
  const stats = [
    { icon: Clock, value: "30+", label: "Years Experience" },
    { icon: Users, value: "100+", label: "Happy Clients" },
    { icon: Award, value: "Fully", label: "Insured" },
  ];

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-[75vh] flex items-center overflow-hidden"
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src={HERO_IMAGE}
          alt="Modern home extension"
          className="w-full h-full object-cover"
        />
        <div className="hero-overlay absolute inset-0" />
      </div>

      {/* Grid Pattern Overlay */}
      <div className="absolute inset-0 grid-pattern opacity-10 pointer-events-none" />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-20">
        <div className="max-w-2xl">
          {/* Technical Label */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-6"
          >
            <span className="inline-block px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white font-mono text-xs uppercase tracking-widest">
              Southport • Merseyside • Lancashire
            </span>
          </motion.div>

          {/* Main Heading */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            data-testid="hero-heading"
            className="font-heading text-5xl md:text-7xl font-bold tracking-tight leading-none text-white mb-6"
          >
            Building<br />
            <span className="text-royal-light">Dreams</span> Since<br />
            1993
          </motion.h1>

          {/* Subheading */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            data-testid="hero-subheading"
            className="text-lg md:text-xl text-slate-300 leading-relaxed mb-10 max-w-lg"
          >
            Family-run building and joinery contractors. Specialists in home extensions, 
            conservatories, windows & doors, kitchens and roofing.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-wrap gap-4 mb-16"
          >
            <button
              onClick={onGetQuoteClick}
              data-testid="hero-cta-primary"
              className="btn-primary px-8 py-4 inline-flex items-center gap-2 text-sm"
            >
              Get a Free Quote
              <ArrowRight size={18} />
            </button>
            <a
              href="#services"
              data-testid="hero-cta-secondary"
              className="px-8 py-4 inline-flex items-center gap-2 text-sm text-white border-2 border-white/70 font-bold uppercase tracking-wide hover:bg-white hover:text-slate-900 transition-all"
            >
              View Our Services
            </a>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="flex flex-wrap gap-8"
          >
            {stats.map((stat, index) => (
              <div
                key={stat.label}
                data-testid={`hero-stat-${index}`}
                className="flex items-center gap-3"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-white/10 backdrop-blur-sm border border-white/20">
                  <stat.icon size={20} className="text-royal-light" />
                </div>
                <div>
                  <div className="stat-number text-2xl text-white">
                    {stat.value}
                  </div>
                  <div className="text-xs text-slate-400 font-mono uppercase tracking-wider">
                    {stat.label}
                  </div>
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1, duration: 0.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
            className="w-1.5 h-1.5 bg-white rounded-full"
          />
        </div>
      </motion.div>
    </section>
  );
};
